import { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SecondaryButton } from '@/components/ui/button-variants';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Search, 
  Users, 
  Heart, 
  Camera, 
  MapPin, 
  UserPlus,
  TrendingUp,
  Crown,
  Star,
  UserMinus,
  Lock,
  MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';
import exploreHeroBg from '@/assets/explore-hero-bg.jpg';

interface UserProfile {
  id: string;
  user_id: string;
  username: string;
  display_name: string | null;
  bio: string | null;
  avatar_url: string | null;
  location: string | null;
  is_private: boolean;
  posts_count: number;
  followers_count: number;
  following_count: number;
  pets_count: number;
  created_at: string;
}

interface TrendingHashtag {
  hashtag: string;
  count: number;
}

interface PostResult {
  id: string;
  user_id: string;
  content: string | null;
  image_url: string | null;
  location: string | null;
  created_at: string;
  likes_count: number;
  comments_count: number;
  pet_id: string | null;
  pet_name?: string;
  pet_avatar?: string;
  profile?: {
    username: string;
    display_name: string | null;
    avatar_url: string | null;
  };
}

interface PetResult {
  id: string;
  user_id: string;
  name: string;
  species: string;
  breed: string | null;
  avatar_url: string | null;
  description: string | null;
  age: number | null;
  profile?: {
    username: string;
    display_name: string | null;
  };
}

const Explore = () => {
  const { user, loading: authLoading } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserProfile[]>([]);
  const [postResults, setPostResults] = useState<PostResult[]>([]);
  const [petResults, setPetResults] = useState<PetResult[]>([]);
  const [suggestedUsers, setSuggestedUsers] = useState<UserProfile[]>([]);
  const [popularUsers, setPopularUsers] = useState<UserProfile[]>([]);
  const [trendingHashtags, setTrendingHashtags] = useState<TrendingHashtag[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchType, setSearchType] = useState<'all' | 'users' | 'posts' | 'pets'>('all');
  const [followingUsers, setFollowingUsers] = useState<Set<string>>(new Set());

  // Redirect to auth if not authenticated
  if (!authLoading && !user) {
    return <Navigate to="/auth" replace />;
  }

  useEffect(() => {
    if (user) {
      fetchSuggestedUsers();
      fetchPopularUsers();
      fetchTrendingHashtags();
      fetchFollowingList();
    }
  }, [user]);

  useEffect(() => {
    if (searchQuery.trim()) {
      const debounceSearch = setTimeout(() => {
        handleSearch();
      }, 300);
      return () => clearTimeout(debounceSearch);
    } else {
      setSearchResults([]);
      setPostResults([]);
      setPetResults([]);
    }
  }, [searchQuery, searchType]); // Add searchType dependency

  const fetchFollowingList = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', user.id);

      if (error) throw error;
      
      const followingSet = new Set(data?.map(f => f.following_id) || []);
      setFollowingUsers(followingSet);
    } catch (error) {
      console.error('Error fetching following list:', error);
    }
  };

  const getUsersWithRealCounts = async (profiles: any[]) => {
    return await Promise.all(
      profiles.map(async (profile) => {
        try {
          const [postsCount, petsCount, followersCount, followingCount] = await Promise.all([
            supabase
              .from('posts')
              .select('id', { count: 'exact', head: true })
              .eq('user_id', profile.user_id)
              .eq('is_public', true),
            supabase
              .from('pets')
              .select('id', { count: 'exact', head: true })
              .eq('user_id', profile.user_id)
              .eq('is_public', true),
            supabase
              .from('follows')
              .select('id', { count: 'exact', head: true })
              .eq('following_id', profile.user_id),
            supabase
              .from('follows')
              .select('id', { count: 'exact', head: true })
              .eq('follower_id', profile.user_id),
          ]);

          return {
            ...profile,
            posts_count: postsCount.count || 0,
            pets_count: petsCount.count || 0,
            followers_count: followersCount.count || 0,
            following_count: followingCount.count || 0,
          };
        } catch (error) {
          console.error('Error fetching counts for profile:', profile.username, error);
          return {
            ...profile,
            posts_count: 0,
            pets_count: 0,
            followers_count: 0,
            following_count: 0,
          };
        }
      })
    );
  };

  const fetchSuggestedUsers = async () => {
    if (!user) return;

    try {
      // Get users that current user is not following and exclude self
      const { data: followingData } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', user.id);

      const followingIds = followingData?.map(f => f.following_id) || [];
      const excludeIds = [...followingIds, user.id];

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .not('user_id', 'in', `(${excludeIds.join(',')})`)
        .limit(6);

      if (error) throw error;

      const usersWithCounts = await getUsersWithRealCounts(data || []);
      setSuggestedUsers(usersWithCounts);
    } catch (error) {
      console.error('Error fetching suggested users:', error);
    }
  };

  const fetchPopularUsers = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .neq('user_id', user.id)
        .limit(10);

      if (error) throw error;

      const usersWithCounts = await getUsersWithRealCounts(data || []);
      // Sort by followers count (real data)
      const sortedUsers = usersWithCounts.sort((a, b) => b.followers_count - a.followers_count);
      
      setPopularUsers(sortedUsers);
    } catch (error) {
      console.error('Error fetching popular users:', error);
    }
  };

  const fetchTrendingHashtags = async () => {
    try {
      // Get real hashtags from posts content from last 7 days for more relevant trending
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const { data: postsData, error } = await supabase
        .from('posts')
        .select('content, created_at, likes_count, comments_count')
        .eq('is_public', true)
        .not('content', 'is', null)
        .gte('created_at', sevenDaysAgo.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      const hashtagData: { [key: string]: { count: number; engagement: number; recentPosts: number } } = {};
      
      postsData?.forEach(post => {
        if (post.content) {
          // More comprehensive hashtag extraction including Italian characters
          const hashtags = post.content.match(/#[a-zA-Z0-9_àáâãäåæçèéêëìíîïðñòóôõöøùúûüýþÿ]+/gi);
          hashtags?.forEach(hashtag => {
            const cleanHashtag = hashtag.toLowerCase();
            const engagement = (post.likes_count || 0) + (post.comments_count || 0);
            
            if (!hashtagData[cleanHashtag]) {
              hashtagData[cleanHashtag] = { count: 0, engagement: 0, recentPosts: 0 };
            }
            hashtagData[cleanHashtag].count += 1;
            hashtagData[cleanHashtag].engagement += engagement;
            
            // More weight for recent posts (last 24 hours)
            const isRecent = new Date(post.created_at) > new Date(Date.now() - 24 * 60 * 60 * 1000);
            if (isRecent) {
              hashtagData[cleanHashtag].recentPosts += 1;
            }
          });
        }
      });

      // Calculate trending score: (count * avg_engagement) + (recent_posts * 2)
      const sortedHashtags = Object.entries(hashtagData)
        .filter(([hashtag, data]) => data.count >= 2) // Min 2 uses to be trending
        .map(([hashtag, data]) => ({
          hashtag,
          count: data.count,
          score: (data.count * (data.engagement / data.count || 1)) + (data.recentPosts * 2)
        }))
        .sort((a, b) => b.score - a.score)
        .slice(0, 12)
        .map(({ hashtag, count }) => ({ hashtag, count }));

      setTrendingHashtags(sortedHashtags);
    } catch (error) {
      console.error('Error fetching trending hashtags:', error);
      setTrendingHashtags([]);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    try {
      // Search users with better filtering
      if (searchType === 'all' || searchType === 'users') {
        const { data: usersData, error: usersError } = await supabase
          .from('profiles')
          .select('*')
          .or(`username.ilike.%${searchQuery}%,display_name.ilike.%${searchQuery}%,bio.ilike.%${searchQuery}%,location.ilike.%${searchQuery}%`)
          .neq('user_id', user?.id || '') // Exclude current user
          .limit(20);

        if (usersError) throw usersError;

        const usersWithCounts = await getUsersWithRealCounts(usersData || []);
        // Sort by relevance: exact matches first, then by followers count
        const sortedUsers = usersWithCounts.sort((a, b) => {
          const aExact = a.username.toLowerCase().includes(searchQuery.toLowerCase()) || 
                        a.display_name?.toLowerCase().includes(searchQuery.toLowerCase());
          const bExact = b.username.toLowerCase().includes(searchQuery.toLowerCase()) || 
                        b.display_name?.toLowerCase().includes(searchQuery.toLowerCase());
          
          if (aExact && !bExact) return -1;
          if (!aExact && bExact) return 1;
          return b.followers_count - a.followers_count;
        });
        setSearchResults(sortedUsers);
      }

      // Search posts with better relevance
      if (searchType === 'all' || searchType === 'posts') {
        const { data: postsData, error: postsError } = await supabase
          .from('posts')
          .select('*')
          .eq('is_public', true)
          .or(`content.ilike.%${searchQuery}%,location.ilike.%${searchQuery}%`)
          .order('created_at', { ascending: false })
          .limit(20);

        if (postsError) throw postsError;

        // Get profiles and sort by engagement (likes + comments)
        const formattedPosts = await Promise.all((postsData || []).map(async (post) => {
          // Get profile data
          const { data: profileData } = await supabase
            .from('profiles')
            .select('username, display_name, avatar_url')
            .eq('user_id', post.user_id)
            .single();

          // Get pet data if pet_id exists
          let petData = null;
          if (post.pet_id) {
            const { data: pet } = await supabase
              .from('pets')
              .select('name, avatar_url')
              .eq('id', post.pet_id)
              .single();
            petData = pet;
          }

          return {
            ...post,
            pet_name: petData?.name,
            pet_avatar: petData?.avatar_url,
            profile: profileData
          };
        }));

        // Sort by engagement score
        const sortedPosts = formattedPosts.sort((a, b) => {
          const scoreA = (a.likes_count || 0) + (a.comments_count || 0);
          const scoreB = (b.likes_count || 0) + (b.comments_count || 0);
          return scoreB - scoreA;
        });

        setPostResults(sortedPosts);
      }

      // Search pets
      if (searchType === 'all' || searchType === 'pets') {
        const { data: petsData, error: petsError } = await supabase
          .from('pets')
          .select('*')
          .eq('is_public', true)
          .or(`name.ilike.%${searchQuery}%,breed.ilike.%${searchQuery}%,species.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`)
          .limit(20);

        if (petsError) throw petsError;

        // Get profiles for these pets
        const formattedPets = await Promise.all((petsData || []).map(async (pet) => {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('username, display_name')
            .eq('user_id', pet.user_id)
            .single();

          return {
            ...pet,
            profile: profileData
          };
        }));

        setPetResults(formattedPets);
      }
    } catch (error) {
      console.error('Error searching:', error);
      toast.error('Errore nella ricerca');
    } finally {
      setLoading(false);
    }
  };

   const handleFollow = async (targetUserId: string, username: string) => {
    if (!user) return;

    try {
      const isFollowing = followingUsers.has(targetUserId);
      
      if (isFollowing) {
        // Unfollow
        const { error } = await supabase
          .from('follows')
          .delete()
          .eq('follower_id', user.id)
          .eq('following_id', targetUserId);

        if (error) throw error;

        setFollowingUsers(prev => {
          const newSet = new Set(prev);
          newSet.delete(targetUserId);
          return newSet;
        });

        toast.success(`Non segui più @${username}`);
      } else {
        // Follow
        const { error } = await supabase
          .from('follows')
          .insert({
            follower_id: user.id,
            following_id: targetUserId,
          });

        if (error) throw error;

        setFollowingUsers(prev => new Set([...prev, targetUserId]));

        toast.success(`Ora segui @${username}!`);
      }
      
      // Refresh data to get updated counts from database
      fetchSuggestedUsers();
      fetchPopularUsers();
    } catch (error) {
      console.error('Error toggling follow:', error);
      toast.error('Errore nell\'operazione');
    }
  };

  const formatCount = (count: number) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  const UserCard = ({ profile }: { profile: UserProfile }) => {
    const isFollowing = followingUsers.has(profile.user_id);
    
    return (
      <Card className="glass border-white/20 hover:border-primary/20 transition-all">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Link to={`/app/user/${profile.username}`}>
              <Avatar className="w-12 h-12 ring-2 ring-primary/20">
                <AvatarImage src={profile.avatar_url || undefined} />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {profile.username.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </Link>
            
            <div className="flex-1 min-w-0">
              <Link to={`/app/user/${profile.username}`} className="block">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-white truncate hover:text-primary transition-colors">
                    {profile.display_name || profile.username}
                  </h3>
                  {profile.is_private && (
                    <Lock className="w-3 h-3 text-muted-foreground" />
                  )}
                </div>
                <p className="text-sm text-muted-foreground">@{profile.username}</p>
              </Link>
              
              {profile.bio && (
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                  {profile.bio}
                </p>
              )}
              
              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                <span>{formatCount(profile.posts_count)} post</span>
                <span>{formatCount(profile.followers_count)} follower</span>
                <span>{formatCount(profile.pets_count)} pet</span>
              </div>
            </div>
            
            <Button
              size="sm"
              variant={isFollowing ? "outline" : "default"}
              onClick={() => handleFollow(profile.user_id, profile.username)}
              className={isFollowing ? "border-primary text-primary hover:bg-primary/10" : ""}
            >
              {isFollowing ? (
                <UserMinus className="w-3 h-3 mr-1" />
              ) : (
                <UserPlus className="w-3 h-3 mr-1" />
              )}
              {isFollowing ? 'Seguendo' : profile.is_private ? 'Richiedi' : 'Segui'}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (authLoading) {
    return (
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        <div className="h-8 w-32 bg-muted rounded animate-pulse" />
        <div className="grid gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-20 bg-muted rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div 
      className="cosmic-background min-h-screen"
      style={{ backgroundImage: `url(${exploreHeroBg})` }}
    >
      <div className="max-w-4xl mx-auto p-4 pb-20 lg:pb-4">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold gradient-text mb-2">Esplora Community</h1>
        <p className="text-muted-foreground">Scopri nuovi amici pet e contenuti interessanti</p>
      </div>

      {/* Search Bar */}
      <Card className="glass border-white/20 mb-6">
        <CardContent className="p-4 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Cerca utenti, post, pets o hashtag..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 glass border-white/20"
            />
          </div>
          
          {/* Search Type Filters */}
          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm"
              variant={searchType === 'all' ? 'default' : 'outline'}
              onClick={() => setSearchType('all')}
              className="text-xs"
            >
              Tutto
            </Button>
            <Button
              size="sm"
              variant={searchType === 'users' ? 'default' : 'outline'}
              onClick={() => setSearchType('users')}
              className="text-xs"
            >
              <Users className="w-3 h-3 mr-1" />
              Utenti
            </Button>
            <Button
              size="sm"
              variant={searchType === 'posts' ? 'default' : 'outline'}
              onClick={() => setSearchType('posts')}
              className="text-xs"
            >
              <Camera className="w-3 h-3 mr-1" />
              Post
            </Button>
            <Button
              size="sm"
              variant={searchType === 'pets' ? 'default' : 'outline'}
              onClick={() => setSearchType('pets')}
              className="text-xs"
            >
              <Heart className="w-3 h-3 mr-1" />
              Pets
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="suggested" className="space-y-6">
        <TabsList className="glass border-white/20">
          <TabsTrigger value="suggested" className="data-[state=active]:bg-primary">
            <Star className="w-4 h-4 mr-2" />
            Suggeriti
          </TabsTrigger>
          <TabsTrigger value="popular" className="data-[state=active]:bg-primary">
            <Crown className="w-4 h-4 mr-2" />
            Popolari
          </TabsTrigger>
          <TabsTrigger value="trending" className="data-[state=active]:bg-primary">
            <TrendingUp className="w-4 h-4 mr-2" />
            Trending
          </TabsTrigger>
        </TabsList>

        {/* Search Results */}
        {searchQuery && (
          <div className="space-y-4">
            {/* Users Results */}
            {(searchType === 'all' || searchType === 'users') && searchResults.length > 0 && (
              <Card className="glass border-white/20">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <Users className="w-5 h-5 mr-2" />
                    Utenti ({searchResults.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {loading ? (
                    <div className="space-y-3">
                      {[...Array(3)].map((_, i) => (
                        <div key={i} className="h-16 bg-muted/20 rounded animate-pulse" />
                      ))}
                    </div>
                  ) : (
                    searchResults.map((profile) => (
                      <UserCard key={profile.id} profile={profile} />
                    ))
                  )}
                </CardContent>
              </Card>
            )}

            {/* Posts Results */}
            {(searchType === 'all' || searchType === 'posts') && postResults.length > 0 && (
              <Card className="glass border-white/20">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <Camera className="w-5 h-5 mr-2" />
                    Post ({postResults.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {postResults.map((post) => (
                    <Card key={post.id} className="glass border-white/10 hover:border-primary/20 transition-all">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <Link to={`/app/user/${post.profile?.username}`}>
                            <Avatar className="w-10 h-10 ring-2 ring-primary/20">
                              <AvatarImage src={post.profile?.avatar_url || undefined} />
                              <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                                {post.profile?.username?.charAt(0).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                          </Link>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <Link to={`/app/user/${post.profile?.username}`} className="font-medium text-white hover:text-primary transition-colors">
                                {post.profile?.display_name || post.profile?.username}
                              </Link>
                              <span className="text-xs text-muted-foreground">
                                {new Date(post.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            
                            {post.content && (
                              <p className="text-sm text-muted-foreground mb-2 line-clamp-3">
                                {post.content}
                              </p>
                            )}
                            
                            {post.image_url && (
                              <div className="rounded-lg overflow-hidden mb-2">
                                <img 
                                  src={post.image_url} 
                                  alt="Post" 
                                  className="w-full h-32 object-cover"
                                />
                              </div>
                            )}
                            
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Heart className="w-3 h-3" />
                                {post.likes_count}
                              </span>
                              <span className="flex items-center gap-1">
                                <MessageCircle className="w-3 h-3" />
                                {post.comments_count}
                              </span>
                              {post.location && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {post.location}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Pets Results */}
            {(searchType === 'all' || searchType === 'pets') && petResults.length > 0 && (
              <Card className="glass border-white/20">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <Heart className="w-5 h-5 mr-2" />
                    Pets ({petResults.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {petResults.map((pet) => (
                    <Card key={pet.id} className="glass border-white/10 hover:border-primary/20 transition-all">
                      <CardContent className="p-4">
                         <div className="flex items-center gap-4">
                           <Link to={`/app/pet/${pet.id}`}>
                             <Avatar className="w-12 h-12 ring-2 ring-primary/20 hover:ring-primary/40 transition-all">
                               <AvatarImage src={pet.avatar_url || undefined} />
                               <AvatarFallback className="bg-primary text-primary-foreground">
                                 {pet.name.charAt(0).toUpperCase()}
                               </AvatarFallback>
                             </Avatar>
                           </Link>
                           
                           <div className="flex-1 min-w-0">
                             <Link to={`/app/pet/${pet.id}`} className="hover:text-primary transition-colors">
                               <h3 className="font-semibold text-white">{pet.name}</h3>
                             </Link>
                             <p className="text-sm text-muted-foreground">
                               {pet.breed ? `${pet.species} - ${pet.breed}` : pet.species}
                             </p>
                             <p className="text-xs text-muted-foreground">
                               di <Link to={`/app/user/${pet.profile?.username}`} className="hover:text-primary transition-colors">
                                 @{pet.profile?.username}
                               </Link>
                             </p>
                             {pet.description && (
                               <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                 {pet.description}
                               </p>
                             )}
                             {pet.age && (
                               <p className="text-xs text-muted-foreground">
                                 {pet.age} {pet.age === 1 ? 'anno' : 'anni'}
                               </p>
                             )}
                           </div>
                         </div>
                      </CardContent>
                    </Card>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* No Results */}
            {loading ? (
              <Card className="glass border-white/20">
                <CardContent className="p-8">
                  <div className="space-y-3">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="h-16 bg-muted/20 rounded animate-pulse" />
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : (!loading && searchResults.length === 0 && postResults.length === 0 && petResults.length === 0) && (
              <Card className="glass border-white/20">
                <CardContent className="p-8">
                  <div className="text-center">
                    <Search className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">Nessun risultato trovato per "{searchQuery}"</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Suggested Users */}
        <TabsContent value="suggested" className="space-y-4">
          
          <Card className="glass border-white/20">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Star className="w-5 h-5 mr-2" />
                Suggeriti per Te
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {suggestedUsers.length > 0 ? (
                suggestedUsers.map((profile) => (
                  <UserCard key={profile.id} profile={profile} />
                ))
              ) : (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">Nessun utente suggerito al momento</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Popular Users */}
        <TabsContent value="popular" className="space-y-4">
          <Card className="glass border-white/20">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Crown className="w-5 h-5 mr-2" />
                Utenti Popolari
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {popularUsers.length > 0 ? (
                popularUsers.map((profile) => (
                  <UserCard key={profile.id} profile={profile} />
                ))
              ) : (
                <div className="text-center py-8">
                  <Crown className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">Caricamento utenti popolari...</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trending */}
        <TabsContent value="trending" className="space-y-4">
          <Card className="glass border-white/20">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <TrendingUp className="w-5 h-5 mr-2" />
                Hashtag di Tendenza
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3">
                {trendingHashtags.map((hashtag, index) => (
                  <div 
                    key={hashtag.hashtag} 
                    className="flex items-center justify-between p-3 rounded-lg glass border border-white/10 hover:border-primary/20 transition-all cursor-pointer group"
                    onClick={() => {
                      setSearchQuery(hashtag.hashtag);
                      setSearchType('posts');
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-primary to-secondary text-white font-bold text-sm">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-semibold text-primary group-hover:text-primary-glow transition-colors">
                          {hashtag.hashtag}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {hashtag.count} post
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-secondary group-hover:scale-110 transition-transform" />
                      <Badge variant="secondary" className="text-xs">
                        Trend #{index + 1}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      </div>
    </div>
  );
};

export default Explore;